import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// GraphQL Query & Mutation
const GET_TAX = gql`
  query {
    getTax {
      sgst
      commission
      updatedAt
    }
  }
`;

const UPDATE_TAX = gql`
  mutation UpdateTax($sgst: Float!, $commission: Float!) {
    updateTax(input: { sgst: $sgst, commission: $commission }) {
      sgst
      commission
      updatedAt
    }
  }
`;

function TaxSettingsForm() {
  const [sgst, setSGST] = useState("");
  const [commission, setCommission] = useState("");
  const [message, setMessage] = useState("");

  const { loading, error, data, refetch } = useQuery(GET_TAX, {
    fetchPolicy: "network-only",
  });

  const [updateTax] = useMutation(UPDATE_TAX);

  const latestTax = data?.getTax;

  // Set local state when tax data is loaded from server
  // Only set initial if user hasn't typed yet, or just display last saved separately
  // Usually better to show current values in input if we want to edit them. 
  // Let's populate inputs if they are empty and data exists, or just use separate display for "Last Saved"
  // The original code only setLatestTax separately and inputs started empty.

  // Submit Mutation
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const { data } = await updateTax({
        variables: {
          sgst: parseFloat(sgst),
          commission: parseFloat(commission),
        },
      });

      if (data?.updateTax) {
        setMessage("✅ Tax rates updated successfully!");
        refetch(); // Refresh latest tax display
      }
    } catch (err) {
      setMessage("❌ Failed to update tax: " + err.message);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🧾 Set SGST & Commission</h2>

      <form onSubmit={handleSubmit}>
        {/* SGST */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>SGST (%)</label>
          <input
            type="number"
            step="0.01"
            value={sgst}
            onChange={(e) => setSGST(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        {/* Commission */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Commission (%)</label>
          <input
            type="number"
            step="0.01"
            value={commission}
            onChange={(e) => setCommission(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <button type="submit" style={styles.button}>💾 Save Tax Rates</button>
      </form>

      {message && <div style={styles.message}>{message}</div>}

      {/* Show loading/error for query */}
      {loading && <p style={{ textAlign: 'center' }}>Loading current rates...</p>}
      {error && <p style={{ textAlign: 'center', color: 'red' }}>Error loading rates</p>}

      {latestTax && (
        <div style={styles.lastSavedBox}>
          📊 <strong>Last Saved Rates:</strong>
          <div>SGST: {latestTax.sgst}% | Commission: {latestTax.commission}%</div>
          <div style={styles.date}>
            Updated: {new Date(latestTax.updatedAt).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 420,
    margin: "60px auto",
    padding: 32,
    borderRadius: 16,
    border: "1px solid #eee",
    background: "white",
    fontFamily: "Inter, sans-serif",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
  },
  title: { textAlign: "center", marginBottom: 25 },
  inputGroup: { marginBottom: 20 },
  label: { display: "block", marginBottom: 8, fontWeight: 500 },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  button: {
    width: "100%",
    padding: 12,
    background: "#1A0A5B",
    color: "white",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  },
  message: { marginTop: 20, textAlign: "center", fontWeight: 500 },
  lastSavedBox: {
    marginTop: 25,
    padding: 18,
    background: "#EEF1FF",
    borderRadius: 10,
    textAlign: "center",
  },
  date: { fontSize: 12, marginTop: 5, opacity: 0.6 },
};

export default TaxSettingsForm;
